
import React, { useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useStudentKpi } from "../hooks/useStudentKpi";
import { devLog } from '@/utils/devLog';
import { StudentKpiCards } from '@/components/students/StudentKpiCards';
import { toast } from 'sonner';
import { StudentKpi } from '@/services/studentKpiService';

interface StudentInsightsPanelProps {
  creatorId: string;
}

export const StudentInsightsPanel: React.FC<StudentInsightsPanelProps> = ({ creatorId }) => {
  devLog(`🔍 StudentInsightsPanel rendering with creator ID: ${creatorId}`);
  const { data: kpi, isLoading, isError, error } = useStudentKpi(creatorId);
  
  useEffect(() => {
    if (isError && error) {
      console.error("Error loading student KPIs:", error);
      toast.error("Failed to load student insights data", {
        description: error.message || "Check developer console for details"
      });
    }
  }, [isError, error]);

  useEffect(() => {
    if (kpi) {
      devLog("📊 Student KPI data received:", kpi);
      console.log("Student KPI data loaded successfully:", {
        totalStudents: kpi.totalStudents,
        newEnrollments: kpi.newEnrollments,
        activeStudents: kpi.activeStudents,
        averageProgress: kpi.averageProgress
      });
    }
  }, [kpi]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {Array(4).fill(0).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <Skeleton className="h-5 w-20 mb-2" />
              <Skeleton className="h-8 w-32" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (isError || !kpi) {
    return (
      <Card className="bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800">
        <CardContent className="p-6">
          <p className="text-amber-600 dark:text-amber-400">
            Unable to load student insights. Please try again later.
          </p>
          <p className="text-xs text-amber-600/80 dark:text-amber-400/80 mt-1">
            {error ? `Error: ${error.message}` : 'No data available.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return <StudentKpiCards kpis={kpi} isLoading={false} periodDays={30} />;
};
